/**
 * The warehouse list's data and the two actions on it.
 *
 * A hook rather than state in the page, so the page is layout: loading the
 * list, filtering it, deactivating one, and taking a newly created one into the
 * list without a refetch.
 */
import { useState, useEffect } from 'react';

import { fetchWarehouses, deactivateWarehouse } from '../../api/warehouse';
import type { Warehouse } from '../../types/shared';

export function useWarehouseList() {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setWarehouses(await fetchWarehouses(false));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load warehouses');
    } finally {
      setLoading(false);
    }
  }

  /** Resolves true when the row is gone, so the caller can close its dialog
   *  only on success and leave it open with the error otherwise. */
  async function remove(id: string): Promise<boolean> {
    setDeleting(true);
    try {
      await deactivateWarehouse(id);
      setWarehouses((rows) => rows.filter((w) => w.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete warehouse');
      return false;
    } finally {
      setDeleting(false);
    }
  }

  const term = searchTerm.toLowerCase();
  const filtered = searchTerm
    ? warehouses.filter(
        (w) =>
          w.name.toLowerCase().includes(term) || (w.code && w.code.toLowerCase().includes(term)),
      )
    : warehouses;

  return {
    warehouses,
    filtered,
    loading,
    error,
    deleting,
    searchTerm,
    setSearchTerm,
    remove,
    add: (w: Warehouse) => setWarehouses((rows) => [...rows, w]),
  };
}
